import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, of, take, tap } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { AppState } from 'src/app/core/store/state';
import {
  ClauseParentPlaybookDto,
  ClauseDetailsField,
  GetClauseDto,
  UpdateClauseDto,
} from 'src/app/modules/clauses/models/clauses.model';
import { ClauseLibraryActions } from 'src/app/modules/clauses/store/actions';
import { ClausesApiService } from 'src/app/modules/clauses/services/clauses-api.service';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import { NotificationsActions } from 'src/app/modules/notifications/store/actions';
import { AppConfigService } from 'src/app/shared/services/app.config.service';
import { ConfigurationApiService } from 'src/app/shared/services/configuration-api.service';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { PositionDto } from 'src/app/shared/services/positions-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { PlaybookDraftDto, PlaybookDto } from '../../models/playbooks.model';
import {
  buildPlaybookPreviewName,
  getPlaybookDisplayLabel,
  PlaybookNameContext,
} from 'src/app/modules/playbooks/playbooks.helper';
import { PlaybooksApiService } from '../../services/playbooks-api.service';
import { PlaybooksActions } from '../../store/actions';

const DRAWER_OPEN_TRANSITION_MS = 220;
type PlaybookOption = PlaybookDto & { displayName: string };

@UntilDestroy()
@Component({
  selector: 'app-playbook-master-clause-drawer',
  templateUrl: './playbook-master-clause-drawer.component.html',
  styleUrl: './playbook-master-clause-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaybookMasterClauseDrawerComponent implements OnInit, OnChanges {
  @Input() public isOpen: boolean = false;
  @Input() public clauseUniqueId: string | null = null;
  @Input() public clauseName: string = '';
  @Input() public focusField: ClauseDetailsField | undefined;
  @Input() public currentPlaybook: PlaybookDto | null = null;
  @Input() public docTypeItems: GetDocumentTypeDto[] = [];
  @Input() public positionItems: PositionDto[] = [];
  @Input() public contentLanguages: LanguageDto[] = [];
  @Output() public closed = new EventEmitter<void>();
  @Output() public clauseUpdated = new EventEmitter<GetClauseDto>();
  @Output() public playbookUpdated = new EventEmitter<PlaybookDto>();

  public form: FormGroup | undefined;
  public linkPlaybookForm: FormGroup | undefined;
  public createPlaybookForm: FormGroup | undefined;
  public constants = constants;
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public focusFields = ClauseDetailsField;
  public isPropertiesExpanded: boolean = true;
  public areLinkedPlaybooksExpanded: boolean = true;
  public creatingNewPlaybook: boolean = false;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public atLeastOneOptionValid: boolean | undefined;
  public clause: GetClauseDto | null = null;
  public linkedPlaybooks: PlaybookDto[] = [];
  public availablePlaybooks: PlaybookOption[] = [];
  public resolvedDocTypeItems: GetDocumentTypeDto[] = [];
  public unlinkingPlaybookIds: string[] = [];
  public canReadClauses = false;
  public canWriteClauses = false;

  private mostDesiredOptionControl!: FormControl<any>;
  private unacceptableOptionControl!: FormControl<any>;
  private fallbackOptionControl!: FormControl<any>;
  private loadedClauseUniqueId: string | null = null;
  private pendingFocusField: ClauseDetailsField | undefined;
  private waitForDrawerSettleBeforeFocus: boolean = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly store$: Store<AppState>,
    private readonly translocoService: TranslocoService,
    private readonly clausesApiService: ClausesApiService,
    private readonly playbooksApiService: PlaybooksApiService,
    private readonly configurationApiService: ConfigurationApiService,
    private readonly appConfigService: AppConfigService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.markForCheck();
    });

    forkJoin({
      canRead: this.permissionService.canRead$(PermissionResource.Clauses).pipe(take(1)),
      canWrite: this.permissionService.canWrite$(PermissionResource.Clauses).pipe(take(1)),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ canRead, canWrite }) => {
        this.canReadClauses = canRead;
        this.canWriteClauses = canWrite;
        this.syncFormAccess();
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen || !this.clauseUniqueId) {
      this.resetState();
      return;
    }

    if (changes['isOpen']) {
      const nextFocusField = changes['focusField']?.currentValue ?? this.focusField;
      this.waitForDrawerSettleBeforeFocus =
        !!changes['isOpen'].currentValue && !changes['isOpen'].previousValue && !!nextFocusField;
    }

    if (changes['currentPlaybook'] && this.clause) {
      const mergedPlaybooks = this.mergeCurrentPlaybook([
        ...this.linkedPlaybooks,
        ...this.availablePlaybooks,
      ]);
      this.linkedPlaybooks = this.getLinkedPlaybooks(mergedPlaybooks);
      this.availablePlaybooks = this.getAvailablePlaybooks(mergedPlaybooks);
      this.cdr.markForCheck();
    }

    if (changes['docTypeItems']) {
      this.resolvedDocTypeItems = [...this.docTypeItems];
    }

    if (changes['focusField'] && this.clause) {
      this.pendingFocusField = this.focusField;
      this.focusRequestedField();
    }

    if (changes['isOpen']?.currentValue || changes['clauseUniqueId']?.currentValue) {
      this.loadDrawerData();
    }
  }

  public toggleProperties(): void {
    this.isPropertiesExpanded = !this.isPropertiesExpanded;
  }

  public toggleLinkedPlaybooks(): void {
    this.areLinkedPlaybooksExpanded = !this.areLinkedPlaybooksExpanded;
  }

  public onConfirm(): void {
    if (!this.canWriteClauses || !this.form || !this.clauseUniqueId || !this.clause) {
      return;
    }

    this.validateOptions();

    if (this.atLeastOneOptionValid === false) {
      this.cdr.markForCheck();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const model = {
      ...this.clause,
      name: this.form.get('clauseName')?.value,
      mostDesiredOptionText: this.form.get('mostDesiredOptionText')?.value,
      fallbackOptionText: this.form.get('fallbackOptionText')?.value,
      unacceptableOptionText: this.form.get('unacceptableOptionText')?.value,
      languageId: this.form.get('languageId')?.value,
      keyTerms: this.clause.keyTerms,
    } as UpdateClauseDto;

    delete model.documentTypes;
    delete model.positions;

    const clauseId = this.clause.uniqueId || this.clauseUniqueId;

    this.isSaving = true;
    this.clausesApiService
      .updateClause$(model, clauseId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (updatedClause) => {
          this.clause = updatedClause;
          this.applyClauseToForm(updatedClause, false);
          this.isSaving = false;
          this.store$.dispatch(ClauseLibraryActions.getClauses());
          this.store$.dispatch(PlaybooksActions.getPlaybooks());
          this.store$.dispatch(
            NotificationsActions.showNotificationComponent({
              icon: constants.icons.greenTick,
              message: this.translocoService.translate('text.clauseUpdated', {
                name: updatedClause.name,
              }),
            }),
          );
          this.clauseUpdated.emit(updatedClause);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isSaving = false;
          this.store$.dispatch(
            NotificationsActions.showNotificationComponent({
              icon: constants.icons.redCross,
              message: this.translocoService.translate('errors.saveMasterClause'),
            }),
          );
          this.cdr.markForCheck();
        },
      });
  }

  public linkToPlaybook(): void {
    if (!this.canWriteClauses || !this.canReadClauses) {
      return;
    }

    const playbookId = this.linkPlaybookForm?.get('playbookId')?.value;

    if (!playbookId || !this.clause?.uniqueId || this.isBusy) {
      return;
    }

    this.isSaving = true;
    this.playbooksApiService
      .linkClausesToPlaybook$(playbookId, [this.clause.uniqueId])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (updatedPlaybook) => {
          const resolvedPlaybook = updatedPlaybook || this.buildLinkedPlaybookFallback(playbookId);

          if (!resolvedPlaybook) {
            this.handlePlaybookMutationError('errors.linkClauseToPlaybook');
            return;
          }

          this.handlePlaybookMutationSuccess(resolvedPlaybook, 'text.playbookUpdated');
          this.linkPlaybookForm?.reset();
        },
        error: () => {
          this.handlePlaybookMutationError('errors.linkClauseToPlaybook');
        },
      });
  }

  public openCreatePlaybookForm(): void {
    if (!this.canWriteClauses) {
      return;
    }

    this.areLinkedPlaybooksExpanded = true;
    if (!this.clause || this.isBusy) {
      return;
    }

    if (!this.createPlaybookForm) {
      this.createPlaybookForm = this.buildCreatePlaybookForm();
    }

    this.creatingNewPlaybook = true;
    this.cdr.markForCheck();
  }

  public onConfirmCreatePlaybook(): void {
    if (!this.canWriteClauses || !this.createPlaybookForm) {
      return;
    }

    if (this.createPlaybookForm.invalid) {
      this.createPlaybookForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    this.createAndLinkPlaybook(this.buildCreatePlaybookDraft());
  }

  public onCancelCreatePlaybookForm(): void {
    this.createPlaybookForm?.reset({
      name: '',
      documentTypeId: null,
      positionId: null,
      languageId: null,
    });
    this.creatingNewPlaybook = false;
    this.cdr.markForCheck();
  }

  public createAndLinkPlaybook(draft: PlaybookDraftDto): void {
    if (!this.canWriteClauses || !this.clause || this.isBusy) {
      return;
    }

    const playbookToSave = {
      uniqueId: crypto.randomUUID(),
      name: draft.name,
      documentTypeId: draft.documentTypeId,
      positionId: draft.positionId,
      languageId: draft.languageId,
      clauses: [this.clause],
      clausesCount: 1,
    } as PlaybookDto;

    this.isSaving = true;
    this.playbooksApiService
      .createPlaybook$(playbookToSave)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (createdPlaybook) => {
          this.handlePlaybookMutationSuccess(createdPlaybook, 'text.playbookAdded');
          this.onCancelCreatePlaybookForm();
        },
        error: () => {
          this.handlePlaybookMutationError('errors.savePlaybook');
        },
      });
  }

  public unlinkFromPlaybook(playbook: PlaybookDto): void {
    if (
      !this.canWriteClauses ||
      !this.clause?.uniqueId ||
      this.isUnlinkingPlaybook(playbook.uniqueId)
    ) {
      return;
    }

    const remainingClauses = (playbook.clauses || []).filter(
      (clause) =>
        this.normalizeClauseId(clause.uniqueId) !== this.normalizeClauseId(this.clause?.uniqueId),
    );
    const updatedPlaybook = {
      ...playbook,
      clauses: remainingClauses,
      clausesCount: remainingClauses.length,
    } as PlaybookDto;

    this.unlinkingPlaybookIds = [...this.unlinkingPlaybookIds, playbook.uniqueId];

    this.playbooksApiService
      .updatePlaybook$(updatedPlaybook)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (savedPlaybook) => {
          this.unlinkingPlaybookIds = this.unlinkingPlaybookIds.filter(
            (playbookId) => playbookId !== savedPlaybook.uniqueId,
          );
          this.handlePlaybookMutationSuccess(savedPlaybook, 'text.playbookUpdated');
        },
        error: () => {
          this.unlinkingPlaybookIds = this.unlinkingPlaybookIds.filter(
            (playbookId) => playbookId !== playbook.uniqueId,
          );
          this.store$.dispatch(
            NotificationsActions.showNotificationComponent({
              icon: constants.icons.redCross,
              message: this.translocoService.translate('errors.unlinkClauseFromPlaybook'),
            }),
          );
          this.cdr.markForCheck();
        },
      });
  }

  public onCancel(): void {
    if (this.isSaving) {
      return;
    }

    this.restorePersistedState();
  }

  public onDrawerClose(): void {
    if (this.isSaving) {
      return;
    }

    this.closed.emit();
  }

  public openPlaybookDetails(playbookUniqueId: string): void {
    if (!playbookUniqueId || this.isSaving) {
      return;
    }

    this.closed.emit();
    this.router.navigate(['/playbooks', playbookUniqueId]);
  }

  public isUnlinkingPlaybook(playbookId: string): boolean {
    return this.unlinkingPlaybookIds.includes(playbookId);
  }

  public get drawerTitle(): string {
    return this.translocoService.translate('titles.masterClause', {
      name: this.clause?.name || this.clauseName || this.translocoService.translate('text.none'),
    });
  }

  public get isBusy(): boolean {
    return this.isLoading || this.isSaving || this.unlinkingPlaybookIds.length > 0;
  }

  public get clauseInstructionsMaxSize(): number {
    return (
      this.appConfigService.playbookClauseMaxSizeInCharacters ??
      this.appConfigService.addClauseInstructionsMaxSizeInCharacters
    );
  }

  public get playbookPreviewName(): string {
    return buildPlaybookPreviewName(this.buildCreatePlaybookDraft(), this.getPlaybookNameContext());
  }

  public buildPreviewName(
    playbook: Pick<PlaybookDto, 'name' | 'documentTypeId' | 'positionId' | 'languageId'>,
  ): string {
    return getPlaybookDisplayLabel(playbook, this.getPlaybookNameContext());
  }

  private getPlaybookNameContext(): PlaybookNameContext {
    return {
      docTypeItems: this.resolvedDocTypeItems,
      positionItems: this.positionItems,
      contentLanguages: this.contentLanguages,
      selectPlaceholder: this.translocoService.translate('placeholders.select'),
      nonePlaceholder: this.translocoService.translate('text.none'),
    };
  }

  private buildCreatePlaybookForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required]],
      documentTypeId: [null, [Validators.required]],
      positionId: [null, [Validators.required]],
      languageId: [null, [Validators.required]],
    });
  }

  private buildCreatePlaybookDraft(): PlaybookDraftDto {
    const formValue = this.createPlaybookForm?.getRawValue() || {};
    const name = formValue.name?.trim() || '';

    return {
      name,
      documentTypeId: formValue.documentTypeId,
      positionId: formValue.positionId,
      languageId: formValue.languageId,
    };
  }

  private loadDrawerData(): void {
    if (!this.clauseUniqueId) {
      return;
    }

    this.pendingFocusField = this.focusField;

    if (this.loadedClauseUniqueId === this.clauseUniqueId && this.form) {
      this.focusRequestedField();
      return;
    }

    this.loadedClauseUniqueId = this.clauseUniqueId;

    this.isLoading = true;
    this.form = undefined;
    this.clause = null;
    this.linkedPlaybooks = [];
    this.availablePlaybooks = [];
    this.resolvedDocTypeItems = [...this.docTypeItems];
    this.createPlaybookForm = this.buildCreatePlaybookForm();
    this.linkPlaybookForm = this.fb.group({
      playbookId: [null, { validators: [Validators.required] }],
    });
    this.syncFormAccess();
    this.creatingNewPlaybook = false;

    forkJoin({
      config: this.ensureClauseInstructionsMaxSizeLoaded$(),
      clause: this.clausesApiService.getClauseById$(this.clauseUniqueId),
      playbooks: this.playbooksApiService.getPlaybooks$(),
    })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({ clause, playbooks }) => {
          this.clause = clause;
          const mergedPlaybooks = this.mergeCurrentPlaybook(playbooks);
          this.linkedPlaybooks = this.getLinkedPlaybooks(mergedPlaybooks);
          this.availablePlaybooks = this.getAvailablePlaybooks(mergedPlaybooks);
          this.buildForm(clause);
          this.isLoading = false;
          this.isPropertiesExpanded = true;
          this.areLinkedPlaybooksExpanded = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.store$.dispatch(
            NotificationsActions.showNotificationComponent({
              icon: constants.icons.redCross,
              message: this.translocoService.translate('errors.loadMasterClause'),
            }),
          );
          this.closed.emit();
          this.cdr.markForCheck();
        },
      });
  }

  private buildForm(clause: GetClauseDto): void {
    this.form = this.fb.group({
      clauseName: ['', { validators: [Validators.required] }],
      mostDesiredOptionText: ['', { validators: [Validators.maxLength(this.clauseInstructionsMaxSize)] }],
      fallbackOptionText: ['', { validators: [Validators.maxLength(this.clauseInstructionsMaxSize)] }],
      unacceptableOptionText: ['', { validators: [Validators.maxLength(this.clauseInstructionsMaxSize)] }],
      languageId: [null, { validators: [Validators.required] }],
    });

    this.creatingNewPlaybook = false;
    this.initControls();
    this.initFormSubscriptions();
    this.applyClauseToForm(clause);
    this.syncFormAccess();
  }

  private syncFormAccess(): void {
    const forms = [this.form, this.linkPlaybookForm, this.createPlaybookForm].filter(
      (form): form is FormGroup => !!form,
    );

    if (this.canWriteClauses) {
      forms.forEach((form) => form.enable({ emitEvent: false }));
      return;
    }

    this.creatingNewPlaybook = false;
    forms.forEach((form) => form.disable({ emitEvent: false }));
  }

  private initControls(): void {
    this.mostDesiredOptionControl = this.form?.get('mostDesiredOptionText') as FormControl;
    this.fallbackOptionControl = this.form?.get('fallbackOptionText') as FormControl;
    this.unacceptableOptionControl = this.form?.get('unacceptableOptionText') as FormControl;
  }

  private initFormSubscriptions(): void {
    this.form?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (
        !this.mostDesiredOptionControl.pristine ||
        !this.fallbackOptionControl.pristine ||
        !this.unacceptableOptionControl.pristine
      ) {
        this.validateOptions();
        this.cdr.markForCheck();
      }
    });
  }

  private validateOptions(): void {
    this.atLeastOneOptionValid =
      `${this.mostDesiredOptionControl.value || ''}`.trim() !== '' ||
      `${this.unacceptableOptionControl.value || ''}`.trim() !== '' ||
      `${this.fallbackOptionControl.value || ''}`.trim() !== '';
  }

  private applyClauseToForm(clause: GetClauseDto, restoreFocus: boolean = true): void {
    if (!this.form) {
      return;
    }

    this.form.reset(
      {
        clauseName: clause.name,
        mostDesiredOptionText: clause.mostDesiredOptionText,
        fallbackOptionText: clause.fallbackOptionText,
        unacceptableOptionText: clause.unacceptableOptionText,
        languageId: clause.languageId,
      },
      { emitEvent: false },
    );

    this.atLeastOneOptionValid = undefined;

    setTimeout(() => {
      this.form?.markAsPristine();
      this.form?.markAsUntouched();

      if (restoreFocus) {
        this.focusRequestedField();
      }

      this.cdr.markForCheck();
    });

    this.cdr.markForCheck();
  }

  private restorePersistedState(): void {
    if (!this.clause) {
      return;
    }

    this.applyClauseToForm(this.clause, false);
  }

  private ensureClauseInstructionsMaxSizeLoaded$() {
    if (
      this.appConfigService.playbookClauseMaxSizeInCharacters != null ||
      this.appConfigService.addClauseInstructionsMaxSizeInCharacters != null
    ) {
      return of(null);
    }

    return this.configurationApiService.getConfiguration$().pipe(
      tap((config) => {
        this.appConfigService.init(config);
      }),
      catchError(() => of(null)),
    );
  }

  private mergeCurrentPlaybook(playbooks: (PlaybookDto | null | undefined)[]): PlaybookDto[] {
    const mergedPlaybooks = playbooks.filter((playbook): playbook is PlaybookDto => !!playbook);

    if (!this.currentPlaybook) {
      return mergedPlaybooks;
    }

    const existingPlaybookIndex = mergedPlaybooks.findIndex(
      (playbook) => playbook.uniqueId === this.currentPlaybook?.uniqueId,
    );

    if (existingPlaybookIndex >= 0) {
      mergedPlaybooks[existingPlaybookIndex] = this.currentPlaybook;
      return mergedPlaybooks;
    }

    return [this.currentPlaybook, ...mergedPlaybooks];
  }

  private getLinkedPlaybooks(playbooks: PlaybookDto[]): PlaybookDto[] {
    return playbooks.filter((playbook) => this.isLinkedPlaybook(playbook));
  }

  private getAvailablePlaybooks(playbooks: PlaybookDto[]): PlaybookOption[] {
    return playbooks
      .filter((playbook) => !this.isLinkedPlaybook(playbook))
      .map((playbook) => this.toPlaybookOption(playbook));
  }

  private toPlaybookOption(playbook: PlaybookDto): PlaybookOption {
    return {
      ...playbook,
      displayName: this.buildPreviewName(playbook),
    };
  }

  private handlePlaybookMutationSuccess(
    playbook: PlaybookDto | null | undefined,
    messageKey: string,
  ): void {
    if (!playbook) {
      this.handlePlaybookMutationError('errors.savePlaybook');
      return;
    }

    const mergedPlaybooks = this.mergeCurrentPlaybook([
      ...this.linkedPlaybooks.filter((item) => item && item.uniqueId !== playbook.uniqueId),
      ...this.availablePlaybooks.filter((item) => item && item.uniqueId !== playbook.uniqueId),
      playbook,
    ]);

    this.syncClauseParentPlaybooks(playbook);
    this.linkedPlaybooks = this.getLinkedPlaybooks(mergedPlaybooks);
    this.availablePlaybooks = this.getAvailablePlaybooks(mergedPlaybooks);
    this.isSaving = false;
    this.store$.dispatch(PlaybooksActions.getPlaybooks());
    this.store$.dispatch(ClauseLibraryActions.getClauses());
    this.store$.dispatch(
      NotificationsActions.showNotificationComponent({
        icon: constants.icons.greenTick,
        message: this.translocoService.translate(messageKey, {
          name: this.buildPreviewName(playbook),
        }),
      }),
    );
    this.playbookUpdated.emit(playbook);
    this.cdr.markForCheck();
  }

  private handlePlaybookMutationError(messageKey: string): void {
    this.isSaving = false;
    this.store$.dispatch(
      NotificationsActions.showNotificationComponent({
        icon: constants.icons.redCross,
        message: this.translocoService.translate(messageKey),
      }),
    );
    this.cdr.markForCheck();
  }

  private buildLinkedPlaybookFallback(playbookId: string): PlaybookDto | null {
    if (!this.clause) {
      return null;
    }

    const existingPlaybook = [
      this.currentPlaybook,
      ...this.linkedPlaybooks,
      ...this.availablePlaybooks,
    ].find((playbook) => `${playbook?.uniqueId}` === `${playbookId}`);

    if (!existingPlaybook) {
      return null;
    }

    const clauses = [
      ...(existingPlaybook.clauses || []).filter(
        (clause) => `${clause?.uniqueId}` !== `${this.clause?.uniqueId}`,
      ),
      this.clause,
    ];

    return {
      ...existingPlaybook,
      clauses,
      clausesCount: Math.max(existingPlaybook.clausesCount || 0, clauses.length),
    };
  }

  private syncClauseParentPlaybooks(playbook: PlaybookDto): void {
    if (!this.clause) {
      return;
    }

    const currentParentPlaybooks = this.clause.parentPlaybooks || [];
    const nextParentPlaybooks = currentParentPlaybooks.filter(
      (parentPlaybook) =>
        this.normalizePlaybookId(parentPlaybook.id) !== this.normalizePlaybookId(playbook.uniqueId),
    );

    if (this.playbookContainsClause(playbook, this.clause.uniqueId || null)) {
      nextParentPlaybooks.push({
        id: playbook.uniqueId,
        label: playbook.name,
        documentType: playbook.documentTypeId,
        positionId: playbook.positionId,
        languageId: playbook.languageId,
      } as ClauseParentPlaybookDto);
    }

    this.clause = {
      ...this.clause,
      parentPlaybooks: nextParentPlaybooks,
      playbooksCount: nextParentPlaybooks.length,
    };
  }

  private focusRequestedField(): void {
    const focusField = this.pendingFocusField;

    if (!focusField) {
      return;
    }

    this.isPropertiesExpanded = focusField !== ClauseDetailsField.Playbooks;
    this.areLinkedPlaybooksExpanded = focusField === ClauseDetailsField.Playbooks;

    const focusDelay = this.waitForDrawerSettleBeforeFocus ? DRAWER_OPEN_TRANSITION_MS : 0;
    this.waitForDrawerSettleBeforeFocus = false;

    setTimeout(() => {
      const fieldElement = this.elementRef.nativeElement.querySelector(
        `[data-focus-target="${focusField}"]`,
      ) as HTMLElement | null;

      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const fieldInputElement = fieldElement?.querySelector(
        'input, textarea, select',
      ) as HTMLElement | null;
      const fieldInteractiveElement = fieldElement?.querySelector(
        '[contenteditable="true"], [role="combobox"], [tabindex]:not([tabindex="-1"])',
      ) as HTMLElement | null;
      const fieldButtonElement = fieldElement?.querySelector('button') as HTMLElement | null;
      const focusableElement = fieldInputElement || fieldInteractiveElement || fieldButtonElement;

      requestAnimationFrame(() => {
        focusableElement?.focus();
      });
    }, focusDelay);
  }

  private playbookContainsClause(playbook: PlaybookDto, clauseId: string | null): boolean {
    if (!clauseId) {
      return false;
    }

    return (playbook.clauses || []).some(
      (clause) => this.normalizeClauseId(clause.uniqueId) === this.normalizeClauseId(clauseId),
    );
  }

  private isLinkedPlaybook(playbook: PlaybookDto): boolean {
    const linkedPlaybookIds = (this.clause?.parentPlaybooks || []).map((parentPlaybook) =>
      this.normalizePlaybookId(parentPlaybook.id),
    );

    if (linkedPlaybookIds.includes(this.normalizePlaybookId(playbook.uniqueId))) {
      return true;
    }

    return this.playbookContainsClause(playbook, this.clause?.uniqueId || null);
  }

  private normalizeClauseId(id: string | null | undefined): string {
    return id == null ? '' : `${id}`;
  }

  private normalizePlaybookId(id: string | null | undefined): string {
    return id == null ? '' : `${id}`;
  }

  private resetState(): void {
    this.form = undefined;
    this.createPlaybookForm = undefined;
    this.clause = null;
    this.linkedPlaybooks = [];
    this.availablePlaybooks = [];
    this.linkPlaybookForm = undefined;
    this.resolvedDocTypeItems = [...this.docTypeItems];
    this.creatingNewPlaybook = false;
    this.isLoading = false;
    this.isSaving = false;
    this.unlinkingPlaybookIds = [];
    this.atLeastOneOptionValid = undefined;
    this.loadedClauseUniqueId = null;
    this.pendingFocusField = undefined;
    this.waitForDrawerSettleBeforeFocus = false;
    this.cdr.markForCheck();
  }
}
